import {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {AlertCircle, CheckCircle2, Loader2, RotateCcw} from 'lucide-react';

import {registerCardByLink} from '../api/billing/cardLink.api';
import {
    describeCardLinkResult,
    describeMissingParams,
    describeNetworkError,
    describeTossFailure,
    linkPagePath,
    readSavedLinkToken,
} from './tossBillingLinkResult';

/**
 * 고유링크 카드 등록의 마지막 단계 — 토스가 돌려보내는 콜백. `/payments/toss/billing/link`
 *
 * 🔑 **URL 에 계정 식별자가 없다.** 서버가 `ticket` 으로 계정을 꺼낸다.
 * 구 경로(`/payments/toss/billing/direct`)는 `accountId` 를 쿼리로 받아서, 값을 바꾸면
 * 남의 계정에 카드를 붙일 수 있었다(`_handover/11_RISK_REGISTER §DP` DP-04).
 *
 * ## alert 대신 화면으로 결과를 보여준다
 *
 * 구 콜백(`TossPaymentBillingDirect.js`)은 `alert` 을 썼고, 실패 분기에 `return` 이 없어
 * **실패 알럿 뒤에 성공 알럿이 이어 떴다**(DP-02). 알럿은 순서를 실수하기 쉽고 닫고 나면
 * 아무 것도 남지 않는다 ⇒ 여기서는 상태를 화면에 남긴다.
 *
 * ## 결과마다 안내와 다음 행동이 다르다 (2026-10-23)
 *
 * 서버가 거절 사유를 코드로 주고(`ALREADY_REGISTERED` / `EXPIRED` / …), 표(`tossBillingLinkResult.js`)가
 * 그것을 문구와 `retry` 로 바꾼다. 재진입인데 카드가 이미 등록된 고객에게는 **완료**라고 말하고,
 * 티켓이 만료·소진된 고객에게는 **"다시 진행" 버튼**을 준다 — 그 버튼이 없으면 고객은 상황을
 * 모른 채 이 화면만 새로고침한다.
 *
 * "다시 진행" 은 링크 화면으로 돌아가 사업자번호 확인부터 한다. 콜백 URL 에는 설계상 링크 토큰이
 * 없으므로, 링크 화면이 결제창으로 가기 전에 같은 탭의 `sessionStorage` 에 남긴 토큰을 쓴다.
 * 토큰이 없으면(다른 탭·저장 차단) 처음 받은 링크를 다시 열라고 안내한다.
 *
 * ⚠️ **effect 는 한 번만 돈다.** 티켓은 1회용이라 두 번 호출하면 두 번째는 서버가 거절한다
 * (카드가 두 장 붙지는 않지만 화면에 실패가 뜬다). React 18 StrictMode 이중 실행 대비로
 * `hasRun` 을 둔다.
 */
export default function TossPaymentBillingLink() {
    const [query] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState({status: 'loading'});
    /**
     * 이미 서버를 불렀는가.
     *
     * ⚠️ `useEffect` 안의 지역변수로는 막을 수 없다 — effect 가 다시 돌면 변수도 새로 만들어진다.
     * 렌더를 넘어 유지되는 `useRef` 여야 한다(React 18 StrictMode 는 개발 모드에서 effect 를 두 번 돌린다).
     */
    const hasRequested = useRef(false);

    useEffect(() => {
        if (hasRequested.current) return undefined;
        hasRequested.current = true;

        const ticket = query.get('ticket');
        const authKey = query.get('authKey');
        const customerKey = query.get('customerKey');
        const code = query.get('code');
        const message = query.get('message');

        // 토스가 실패를 알려온 경우(고객이 창을 닫았거나 카드사 거절).
        if (code || message) {
            setState(describeTossFailure(message));
            return undefined;
        }

        if (!ticket || !authKey || !customerKey) {
            setState(describeMissingParams());
            return undefined;
        }

        /*
          기본 결제 카드 설정 여부는 **앞 화면(`/card/register`)의 확인창 답**이고,
          토스 결제창을 거쳐 돌아오므로 successUrl 쿼리로 실려 온다(2026-09-10).
          🔑 값이 없으면 `false` = 종전과 같은 동작(구 링크로 들어온 경우에도 안전하다).
        */
        const setDefault = query.get('setDefault') === 'true';

        registerCardByLink(customerKey, authKey, ticket, setDefault)
            .then((res) => setState(describeCardLinkResult(res?.data, {setDefault})))
            .catch(() => setState(describeNetworkError()));

        return undefined;
    }, [query]);

    /* "다시 진행" — 링크 화면으로. 토큰이 없으면 버튼 대신 안내문. */
    const savedToken = readSavedLinkToken();
    const retryPath = linkPagePath(savedToken);

    const renderRetry = () => {
        if (state.retry === 'reload') {
            return (
                <button type="button" onClick={() => window.location.reload()}
                        className="mt-2 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700">
                    <RotateCcw className="w-4 h-4"/> 결과 다시 확인
                </button>
            );
        }
        if (state.retry === 'link') {
            if (retryPath) {
                return (
                    <button type="button" onClick={() => navigate(retryPath, {replace: true})}
                            className="mt-2 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700">
                        <RotateCcw className="w-4 h-4"/> 다시 진행하기
                    </button>
                );
            }
            return (
                <p className="mt-2 text-[13px] text-gray-600">
                    문자·카카오톡으로 받은 <b>처음 링크를 다시 열어</b> 사업자번호 확인부터 진행해주세요.
                </p>
            );
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-12">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-sm p-7 text-center">
                {state.status === 'loading' && (
                    <div className="flex flex-col items-center gap-3 py-10 text-gray-500">
                        <Loader2 className="w-7 h-7 animate-spin text-blue-600"/>
                        <p className="text-[14px]">카드를 등록하는 중입니다. 창을 닫지 말아주세요.</p>
                    </div>
                )}

                {state.status === 'ok' && (
                    <div className="flex flex-col items-center gap-3 py-10">
                        <CheckCircle2 className="w-10 h-10 text-emerald-500"/>
                        <p className="text-[16px] font-bold text-gray-900">{state.title}</p>
                        {state.note && (
                            <p className="text-[13px] text-blue-700 font-medium">{state.note}</p>
                        )}
                        <p className="text-[13px] text-gray-500 leading-relaxed">{state.message}</p>
                    </div>
                )}

                {state.status === 'fail' && (
                    <div className="flex flex-col items-center gap-3 py-10">
                        <AlertCircle className="w-10 h-10 text-red-500"/>
                        <p className="text-[15px] font-bold text-gray-900">{state.title}</p>
                        <p className="text-[13px] text-gray-500 leading-relaxed">{state.message}</p>
                        {renderRetry()}
                    </div>
                )}
            </div>
        </div>
    );
}
